import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { User, Globe, Smile, UserPlus, CheckCircle2, Ban, TrendingUp, Wallet, ShoppingBag } from 'lucide-react';

const MerchantDashboard = () => {
  const [fromDate, setFromDate] = useState('2025-10-01');
  const [toDate, setToDate] = useState('2025-10-30');
  const [branch, setBranch] = useState('All Branches');

  // Online Users Data
  const onlineTicketData = {
    localAdult: { count: 245, revenue: 367500, void: 15 },
    foreignAdult: { count: 180, revenue: 540000, void: 12 },
    localChild: { count: 156, revenue: 156000, void: 8 },
    foreignChild: { count: 98, revenue: 245000, void: 10 }
  };

  // POS Users Data
  const posTicketData = {
    localAdult: { count: 320, revenue: 480000, void: 20 },
    foreignAdult: { count: 210, revenue: 630000, void: 15 },
    localChild: { count: 180, revenue: 180000, void: 12 },
    foreignChild: { count: 120, revenue: 300000, void: 8 }
  };

  // Void status data
  const voidData = [
    { name: 'Void Tickets', value: 45, color: '#10b981' },
    { name: 'Not Yet Void', value: 23, color: '#f59e0b' }
  ];

  // === NEW: Country-wise foreign arrival data (example data) ===
  const countryRawData = [
    { country: 'India', visitors: 285 },
    { country: 'China', visitors: 195 },
    { country: 'Russia', visitors: 98 },
    { country: 'Germany', visitors: 76 },
    { country: 'UK', visitors: 64 },
    { country: 'France', visitors: 52 },
    { country: 'USA', visitors: 48 },
    { country: 'Australia', visitors: 39 },
    { country: 'Japan', visitors: 31 },
    { country: 'Italy', visitors: 28 }
  ];

  // Process: Take top 2 countries, rest → Others
  const sortedCountries = [...countryRawData].sort((a, b) => b.visitors - a.visitors);
  const top2 = sortedCountries.slice(0, 2);
  const othersCount = sortedCountries.slice(2).reduce((sum, c) => sum + c.visitors, 0);

  const countryPieData = [
    ...top2.map(c => ({ name: c.country, value: c.visitors })),
    { name: 'Others', value: othersCount }
  ];

  const COUNTRY_COLORS = ['#0055A5', '#d97706', '#94a3b8']; // Top2 unique, Others gray

  // Custom label for pie chart (shows label + % only for top 2 and Others)
  const renderCustomLabel = ({ name, percent }) => {
    return `${name} ${(percent * 100).toFixed(1)}%`;
  };

  // Calculate totals
  const onlineTotalRevenue = Object.values(onlineTicketData).reduce((sum, item) => sum + item.revenue, 0);
  const onlineTotalCount = Object.values(onlineTicketData).reduce((sum, item) => sum + item.count, 0);
  const onlineTotalVoid = Object.values(onlineTicketData).reduce((sum, item) => sum + item.void, 0);

  const posTotalRevenue = Object.values(posTicketData).reduce((sum, item) => sum + item.revenue, 0);
  const posTotalCount = Object.values(posTicketData).reduce((sum, item) => sum + item.count, 0);
  const posTotalVoid = Object.values(posTicketData).reduce((sum, item) => sum + item.void, 0);

  const totalRevenue = onlineTotalRevenue + posTotalRevenue;
  const totalPurchased = onlineTotalCount + posTotalCount;
  const totalVoid = onlineTotalVoid + posTotalVoid;

  // Revenue distribution data for pie chart
  const revenueDistributionData = [
    { name: 'Online Users', value: onlineTotalRevenue },
    { name: 'POS Users', value: posTotalRevenue }
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
  const DISTRIBUTION_COLORS = ['#0055A5', '#00918f'];

  const handleViewOrderList = () => {
    window.location.href = '/CMSList';
  };

  return (
    <div className="space-y-6">
      {/* Title and Button */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Merchant Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Track your ticket sales performance and revenue insights</p>
        </div>
        <button
          onClick={handleViewOrderList}
          className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark transition-colors"
        >
          View Order List
        </button>
      </div>

      {/* Date Filter */}
      <div className="flex flex-wrap items-center gap-6 p-5 bg-white border rounded-xl border-slate-200">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">From Date</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">To Date</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">Branch</span>
          <select
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            className="px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
          >
            <option>All Branches</option>
            <option>Branch A</option>
            <option>Branch B</option>
            <option>Branch C</option>
            <option>Branch D</option>
          </select>
        </div>
      </div>

      {/* Total Revenue Card */}
      <div className="p-8 text-white rounded-xl bg-brand-blue">
        <h2 className="text-sm font-semibold opacity-90">Total Revenue</h2>
        <div className="mt-2 text-4xl font-bold">Rs. {totalRevenue.toLocaleString()}</div>
        <div className="mt-2 text-sm opacity-90">
          Total Tickets: {totalPurchased} &middot; Total Void: {totalVoid}
        </div>
      </div>

        {/* Online Users Section */}
        <div>
          <h2 className="mb-4 text-lg font-bold text-slate-900">Online Users</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* Online Revenue Cards */}
            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Local Adult</h3>
                <div className="p-2 rounded-lg bg-brand-blue/10">
                  <User className="w-4 h-4 text-brand-blue" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {onlineTicketData.localAdult.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {onlineTicketData.localAdult.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {onlineTicketData.localAdult.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Foreign Adult</h3>
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Globe className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {onlineTicketData.foreignAdult.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {onlineTicketData.foreignAdult.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {onlineTicketData.foreignAdult.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Local Child</h3>
                <div className="p-2 rounded-lg bg-amber-50">
                  <Smile className="w-4 h-4 text-amber-600" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {onlineTicketData.localChild.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {onlineTicketData.localChild.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {onlineTicketData.localChild.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Foreign Child</h3>
                <div className="p-2 rounded-lg bg-red-50">
                  <UserPlus className="w-4 h-4 text-red-500" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {onlineTicketData.foreignChild.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {onlineTicketData.foreignChild.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {onlineTicketData.foreignChild.void}</div>
            </div>

            {/* Total Online Revenue */}
            <div className="p-5 text-white rounded-xl bg-brand-blue">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium opacity-90">Total Online</h3>
                <div className="p-2 bg-white rounded-lg">
                  <Wallet className="w-4 h-4 text-brand-blue" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold">Rs. {onlineTotalRevenue.toLocaleString()}</div>
              <div className="text-xs opacity-90">Purchased: {onlineTotalCount}</div>
              <div className="text-xs opacity-90">Void: {onlineTotalVoid}</div>
            </div>
          </div>
        </div>

        {/* POS Users Section */}
        <div>
          <h2 className="mb-4 text-lg font-bold text-slate-900">POS Users</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* POS Revenue Cards */}
            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Local Adult</h3>
                <div className="p-2 rounded-lg bg-brand-blue/10">
                  <User className="w-4 h-4 text-brand-blue" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {posTicketData.localAdult.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {posTicketData.localAdult.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {posTicketData.localAdult.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Foreign Adult</h3>
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Globe className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {posTicketData.foreignAdult.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {posTicketData.foreignAdult.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {posTicketData.foreignAdult.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Local Child</h3>
                <div className="p-2 rounded-lg bg-amber-50">
                  <Smile className="w-4 h-4 text-amber-600" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {posTicketData.localChild.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {posTicketData.localChild.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {posTicketData.localChild.void}</div>
            </div>

            <div className="p-5 bg-white border rounded-xl border-slate-200">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500">Foreign Child</h3>
                <div className="p-2 rounded-lg bg-red-50">
                  <UserPlus className="w-4 h-4 text-red-500" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold text-slate-900">Rs. {posTicketData.foreignChild.revenue.toLocaleString()}</div>
              <div className="text-xs text-slate-500">Purchased: {posTicketData.foreignChild.count}</div>
              <div className="text-xs font-medium text-emerald-600">Void: {posTicketData.foreignChild.void}</div>
            </div>

            {/* Total POS Revenue */}
            <div className="p-5 text-white rounded-xl bg-emerald-700">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium opacity-90">Total POS</h3>
                <div className="p-2 bg-white rounded-lg">
                  <ShoppingBag className="w-4 h-4 text-emerald-700" />
                </div>
              </div>
              <div className="mb-1 text-xl font-bold">Rs. {posTotalRevenue.toLocaleString()}</div>
              <div className="text-xs opacity-90">Purchased: {posTotalCount}</div>
              <div className="text-xs opacity-90">Void: {posTotalVoid}</div>
            </div>
          </div>
        </div>

        {/* Tickets Overview Section */}
        <div>
          <h2 className="mb-4 text-lg font-bold text-slate-900">Tickets Overview</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Total Purchased */}
            <div className="p-5 bg-white border-l-4 rounded-xl border-y border-r border-slate-200 border-l-emerald-500">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-600">Total Purchased Tickets</h3>
                <div className="p-2.5 bg-emerald-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="mb-2 text-3xl font-bold text-slate-900">{totalPurchased}</div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Online: <span className="font-semibold text-slate-700">{onlineTotalCount}</span></span>
                <span className="text-slate-500">POS: <span className="font-semibold text-slate-700">{posTotalCount}</span></span>
              </div>
            </div>

            {/* Total Void */}
            <div className="p-5 bg-white border-l-4 rounded-xl border-y border-r border-slate-200 border-l-amber-500">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-600">Total Void Tickets</h3>
                <div className="p-2.5 bg-amber-50 rounded-lg">
                  <Ban className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <div className="mb-2 text-3xl font-bold text-slate-900">{totalVoid}</div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-500">Online: <span className="font-semibold text-slate-700">{onlineTotalVoid}</span></span>
                <span className="text-slate-500">POS: <span className="font-semibold text-slate-700">{posTotalVoid}</span></span>
              </div>
            </div>

            {/* Void Rate */}
            <div className="p-5 bg-white border-l-4 rounded-xl border-y border-r border-slate-200 border-l-brand-blue">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-600">Ticket Void Rate</h3>
                <div className="p-2.5 bg-brand-blue/10 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-brand-blue" />
                </div>
              </div>
              <div className="mb-2 text-3xl font-bold text-slate-900">
                {totalPurchased > 0 ? ((totalVoid / totalPurchased) * 100).toFixed(1) : 0}%
              </div>
              <div className="text-xs text-slate-500">
                Void Tickets: <span className="font-semibold text-slate-700">{totalVoid}</span> of {totalPurchased}
              </div>
            </div>
          </div>
        </div>

        {/* Category-wise Details */}
        <div>
          <h2 className="mb-4 text-lg font-bold text-slate-900">Category-wise Details</h2>
          <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th rowSpan="2" className="px-6 py-4 text-sm font-semibold text-left border-r text-slate-700 border-slate-200">Category</th>
                    <th colSpan="2" className="px-6 py-2 text-sm font-semibold text-center border-b border-r text-slate-700 border-slate-200 bg-brand-blue/5">Online</th>
                    <th colSpan="2" className="px-6 py-2 text-sm font-semibold text-center border-b border-r text-slate-700 border-slate-200 bg-emerald-50">POS</th>
                    <th colSpan="2" className="px-6 py-2 text-sm font-semibold text-center border-b text-slate-700 border-slate-200 bg-slate-100">Total</th>
                  </tr>
                  <tr>
                    <th className="px-6 py-2 text-xs font-medium text-center border-r text-slate-600 border-slate-200 bg-brand-blue/5">Purchased</th>
                    <th className="px-6 py-2 text-xs font-medium text-center border-r text-emerald-700 border-slate-200 bg-brand-blue/5">Void</th>
                    <th className="px-6 py-2 text-xs font-medium text-center border-r text-slate-600 border-slate-200 bg-emerald-50">Purchased</th>
                    <th className="px-6 py-2 text-xs font-medium text-center border-r text-emerald-700 border-slate-200 bg-emerald-50">Void</th>
                    <th className="px-6 py-2 text-xs font-medium text-center border-r text-slate-600 border-slate-200 bg-slate-100">Purchased</th>
                    <th className="px-6 py-2 text-xs font-medium text-center text-emerald-700 bg-slate-100">Void</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="hover:bg-slate-50">
                    <td className="px-6 py-3 text-sm font-medium border-r text-slate-800 border-slate-200">Local Adult</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.localAdult.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.localAdult.void}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-emerald-50">{posTicketData.localAdult.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-emerald-50">{posTicketData.localAdult.void}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-center border-r text-slate-900 border-slate-200 bg-slate-50">
                      {onlineTicketData.localAdult.count + posTicketData.localAdult.count}
                    </td>
                    <td className="px-6 py-3 text-sm font-semibold text-center text-emerald-600 bg-slate-50">
                      {onlineTicketData.localAdult.void + posTicketData.localAdult.void}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="px-6 py-3 text-sm font-medium border-r text-slate-800 border-slate-200">Foreign Adult</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.foreignAdult.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.foreignAdult.void}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-emerald-50">{posTicketData.foreignAdult.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-emerald-50">{posTicketData.foreignAdult.void}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-center border-r text-slate-900 border-slate-200 bg-slate-50">
                      {onlineTicketData.foreignAdult.count + posTicketData.foreignAdult.count}
                    </td>
                    <td className="px-6 py-3 text-sm font-semibold text-center text-emerald-600 bg-slate-50">
                      {onlineTicketData.foreignAdult.void + posTicketData.foreignAdult.void}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="px-6 py-3 text-sm font-medium border-r text-slate-800 border-slate-200">Local Child</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.localChild.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.localChild.void}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-emerald-50">{posTicketData.localChild.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-emerald-50">{posTicketData.localChild.void}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-center border-r text-slate-900 border-slate-200 bg-slate-50">
                      {onlineTicketData.localChild.count + posTicketData.localChild.count}
                    </td>
                    <td className="px-6 py-3 text-sm font-semibold text-center text-emerald-600 bg-slate-50">
                      {onlineTicketData.localChild.void + posTicketData.localChild.void}
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="px-6 py-3 text-sm font-medium border-r text-slate-800 border-slate-200">Foreign Child</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.foreignChild.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-brand-blue/5">{onlineTicketData.foreignChild.void}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-600 border-slate-200 bg-emerald-50">{posTicketData.foreignChild.count}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-600 border-slate-200 bg-emerald-50">{posTicketData.foreignChild.void}</td>
                    <td className="px-6 py-3 text-sm font-semibold text-center border-r text-slate-900 border-slate-200 bg-slate-50">
                      {onlineTicketData.foreignChild.count + posTicketData.foreignChild.count}
                    </td>
                    <td className="px-6 py-3 text-sm font-semibold text-center text-emerald-600 bg-slate-50">
                      {onlineTicketData.foreignChild.void + posTicketData.foreignChild.void}
                    </td>
                  </tr>
                  <tr className="font-semibold bg-slate-100">
                    <td className="px-6 py-3 text-sm border-r text-slate-900 border-slate-200">Total</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-900 border-slate-200">{onlineTotalCount}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-700 border-slate-200">{onlineTotalVoid}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-900 border-slate-200">{posTotalCount}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center border-r text-emerald-700 border-slate-200">{posTotalVoid}</td>
                    <td className="px-6 py-3 text-sm text-center border-r text-slate-900 border-slate-200">{totalPurchased}</td>
                    <td className="px-6 py-3 text-sm font-medium text-center text-emerald-700">{totalVoid}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Charts Section - Now with 3 charts */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Ticket Void Status */}
          <div className="p-5 bg-white border rounded-xl border-slate-200">
            <h2 className="mb-4 text-sm font-semibold text-slate-800">Ticket Void Status</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={voidData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip formatter={(value) => `${value} tickets`} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {voidData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                  <span className="text-xs text-slate-500">Void</span>
                </div>
                <div className="text-xl font-bold text-slate-900">{voidData[0].value}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                  <span className="text-xs text-slate-500">Not Yet Void</span>
                </div>
                <div className="text-xl font-bold text-slate-900">{voidData[1].value}</div>
              </div>
            </div>
          </div>

          {/* Revenue Distribution: Online vs POS */}
          <div className="p-5 bg-white border rounded-xl border-slate-200">
            <h2 className="mb-4 text-sm font-semibold text-slate-800">Revenue Distribution</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={revenueDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={4}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {revenueDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={DISTRIBUTION_COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `Rs. ${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DISTRIBUTION_COLORS[0] }}></div>
                  <span className="text-xs text-slate-500">Online Users</span>
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {((onlineTotalRevenue / totalRevenue) * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500">Rs. {onlineTotalRevenue.toLocaleString()}</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DISTRIBUTION_COLORS[1] }}></div>
                  <span className="text-xs text-slate-500">POS Users</span>
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {((posTotalRevenue / totalRevenue) * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-slate-500">Rs. {posTotalRevenue.toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Country-wise Arrival Pie Chart */}
          <div className="p-5 bg-white border rounded-xl border-slate-200">
            <h2 className="mb-4 text-sm font-semibold text-slate-800">Country-wise Arrival</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={countryPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  paddingAngle={4}
                  dataKey="value"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {countryPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COUNTRY_COLORS[index % COUNTRY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} visitors`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-3 mt-4 text-center">
              {countryPieData.map((item, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <div className="rounded-full w-2.5 h-2.5" style={{ backgroundColor: COUNTRY_COLORS[idx] }}></div>
                    <span className="text-xs text-slate-500">{item.name}</span>
                  </div>
                  <div className="text-base font-bold text-slate-900">{item.value}</div>
                  <div className="text-xs text-slate-500">
                    {((item.value / countryRawData.reduce((s, c) => s + c.visitors, 0)) * 100).toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
};

export default MerchantDashboard;